import { Body, Box, Heading, Sheet } from '@hover/blueprint';
import React, { useContext, useState } from 'react';
import { Trans } from 'react-i18next';

import {
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import type { PasswordFormData } from 'src/modules/onboarding/components/Password';
import { PasswordFormFields } from 'src/modules/onboarding/components/Password';
import { makePasswordValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { LinkExternal } from 'src/shared/components';
import type { LinkExternalProps } from 'src/shared/components/LinkExternal';
import { Form } from 'src/shared/components/forms';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useAuthenticateMutation } from 'src/shared/graphql/generated';
import { useRefreshSession } from 'src/shared/graphql/hooks';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { Route } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';
import { isNetworkError } from 'src/shared/utils/graphql';

const FooterLink = (props: LinkExternalProps) => (
  <LinkExternal
    _active={{ color: 'inherit', textDecorationColor: 'inherit' }}
    color="inherit"
    fontWeight="body"
    size={300}
    textDecoration="underline"
    {...props}
  />
);

const FooterText = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  return (
    <Trans
      components={{
        SignUpLink: (
          <FooterLink href={`${Route.SIGN_UP_ONBOARDING.path}/signup`} />
        ),
      }}
      i18nKey="embed.welcomeFooterText"
      t={t}
    />
  );
};

const EmbedWelcomeSheet = ({
  isOpen = false,
  onClose,
}: {
  isOpen?: boolean;
  onClose: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const ht = useHomeownerTranslate();
  const [authenticate] = useAuthenticateMutation();
  const [_state, dispatch] = useContext(OnboardingContext);
  const { userEmail } = useEmbedContext();
  const { setCurrentMenu } = useHomeownerAppContext();
  const refreshSession = useRefreshSession();

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hasNeworkError, setHasNetworkError] = useState(false);
  const [hasError, setHasError] = useState(false);

  const validationSchema = makePasswordValidationSchema(t);

  const onSubmit = async (formData: PasswordFormData) => {
    const { email, password } = formData;
    if (!email || !password) return;

    dispatch({ email });
    setIsAuthenticating(true);

    try {
      const { data } = await authenticate({
        variables: { email, password },
      });
      if (data?.authenticate?.user) {
        setIsAuthenticating(false);
        const {
          jwt: { accessToken, refreshToken },
        } = data.authenticate;
        await defaultLocalStorageAdapter.setAccessToken(accessToken);
        await defaultLocalStorageAdapter.setRefreshToken(refreshToken);
        await refreshSession();
      }

      onClose();
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
    } catch (e) {
      setIsAuthenticating(false);
      if (isNetworkError(e)) {
        setHasNetworkError(true);
      } else {
        setHasError(true);
        logError(e as Error);
      }
    }
  };

  return (
    <Sheet.Container
      autoFocus={false}
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      variant="bottom"
    >
      <Box alignItems="center" justifyContent="center" mx={500}>
        <Box alignItems="center" flexDirection="column" width="750">
          <Heading color="text.primary" size={600} textAlign="center">
            {t('onboarding.welcomeBack')}
          </Heading>
          <Body color="neutral.400" size={300}>
            {ht('embed.logInToDesign')}
          </Body>

          <Box flexDirection="column" mt={200}>
            <Form<PasswordFormData>
              display="flex"
              height="100%"
              initialValues={{ email: userEmail ?? '', password: '' }}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              <PasswordFormFields
                hasError={hasError}
                hasNetworkError={hasNeworkError}
                isAuthenticating={isAuthenticating}
                onForgotPassword={() =>
                  window.open(`${Route.SIGN_UP_ONBOARDING.path}/reset`)
                }
              />
            </Form>
          </Box>

          <Body color="neutral.400" size={300}>
            <FooterText />
          </Body>
        </Box>
      </Box>
    </Sheet.Container>
  );
};

export { EmbedWelcomeSheet };
